import { useSelector } from 'react-redux'
import { BueroType } from '../../../../../../types/Survey/Analysis/Calculation'
import props from '../../../../../../redux/props'
import _ from 'lodash'
import ValueInput from '../helperElements/ValueInput'
import { Concept, ConceptDataCluster, ConceptValues, FloorMappingEntryType } from '../../../../../../types/Survey/Analysis/Concept'
import ValueDropdown from '../helperElements/ValueDropdown'
import SurveyResults from './SurveyResults/SurveyResults'
import OtherInformation from './OtherInformation/OtherInformation'
import { PrebuildType } from '../../../../../../types/Survey/Element'
import AdditionalConfiguration from './AdditionalConfiguration/AdditionalConfiguration'
import Sonderbedarfe from './Sonderbedarfe/Sonderbedarfe'
import getSums from './SurveyResults/getSums'
import FloorAssignment from '../../../../../UI/FloorManager/FloorAssignment'
import ValueTextInput from '../helperElements/ValueTextInput'
import { calculateAnwesenheitPercentage, calculateDesksharingPercentage } from '../../../../../hooks/useAnalysisCalculation/calculateResults'
import { ProductionDistance } from '../../../../../../types/OrganizationUnit'
import Splitting from './Splitting/Splitting'
import { getStaufleacheLaufmeter } from '../../../../../hooks/useAnalysisCalculation/byBueroType/helper/getAnswerValues'
type Props = {
  conceptDataCluster: ConceptDataCluster
  values: ConceptValues
  concept: Concept
  updateValues?: (values: ConceptValues) => void
  index: number
}

const UnitCalculation: React.FC<Props> = ({ conceptDataCluster, values, updateValues, concept }) => {
  const t = useSelector((s) => s[props.TRANSLATION])
  const sums = getSums(values.ergebnisse)

  const updateValue = (key: string) => {
    return (value: any) => {
      const newValues = _.cloneDeep(values)

      _.set(newValues, key, value)

      if (key === 'anwesenheitPercentage') {
        const anwesenheit = _.cloneDeep(newValues.anwesenheitPercentage)
        anwesenheit.percentage = value
        _.set(newValues, 'anwesenheitPercentage', anwesenheit)
      } else if (key === 'bueroType') {
        const anwesenheitPercentage = calculateAnwesenheitPercentage(
          conceptDataCluster.teamSurvey,
          conceptDataCluster.allTeamAnalysis.find((a) => a.organizationUnitUUID === newValues.unitUUID),
          newValues.bueroType,
          {
            ...newValues,
            anwesenheitPercentage: null,
          },
        )
        const desksharingPercentage = calculateDesksharingPercentage(
          conceptDataCluster.teamSurvey,
          conceptDataCluster.allTeamAnalysis.find((a) => a.organizationUnitUUID === newValues.unitUUID),
          newValues.bueroType,
          {
            ...newValues,
            anwesenheitPercentage: anwesenheitPercentage,
            desksharingPercentage: null,
          },
        )
        _.set(newValues, 'anwesenheitPercentage', anwesenheitPercentage)
        _.set(newValues, 'desksharingPercentage', desksharingPercentage)
      }
      updateValues(newValues)
    }
  }

  const pushValue = (key: string, value: any) => {
    const currentValue = values[key]
    currentValue.push(value)
    let newValues = _.set(values, key, currentValue)
    updateValues(newValues)
  }

  const removeValue = (key: string, index: number) => () => {
    const currentValue = values[key]
    currentValue.splice(index, 1)
    let newValues = _.set(values, key, currentValue)
    updateValues(newValues)
  }

  const getDesksharingFactor = (quote: number) => {
    return quote / 100
  }

  const setDesksharingQuote = (factor: number) => {
    const desksharing = _.cloneDeep(values.desksharingPercentage)
    desksharing.percentage = factor * 100
    updateValues(_.set(values, 'desksharingPercentage', desksharing))
  }

  return (
    <div className="pb-2">
      <div className="flex flex-col gap-3">
        <Splitting values={values} concept={concept} />
        <ValueTextInput label={t.survey.analysis.calculation.values.name.headline} value={values.name} onChange={updateValue('name')} />
        <FloorAssignment type={FloorMappingEntryType.UNIT} targetUUID={values.unitUUID} />
        <ValueDropdown
          label={t.survey.analysis.calculation.values.modell.headline}
          items={Object.values(BueroType).map((key) => ({ value: key, label: t.survey.bueroType.types[key] }))}
          value={values.bueroType}
          onChange={updateValue('bueroType')}
        />
        <ValueDropdown
          label={t.survey.analysis.calculation.values.productionDistance.headline}
          items={Object.values(ProductionDistance).map((key) => ({ value: key, label: t.UI.organizationUnitManager.productionDistance[key] }))}
          value={values.productionDistance}
          onChange={updateValue('productionDistance')}
        />
        <ValueInput label={t.survey.analysis.calculation.values.mitarbeiter.ist} value={values.originalMitarbeiter} />
        <ValueInput
          label={t.survey.analysis.calculation.values.mitarbeiter.soll}
          originalValue={values.originalMitarbeiter}
          value={values.mitarbeiter}
          onChange={updateValue('mitarbeiter')}
        />
        <ValueInput
          label={t.survey.analysis.calculation.values.anwesenheit.ist}
          value={values.anwesenheitPercentage.originalPercentage}
          suffix={t.generic.percent}
          decimal
        />
        <ValueInput
          label={t.survey.analysis.calculation.values.anwesenheit.soll}
          value={values.anwesenheitPercentage.percentage}
          onChange={updateValue('anwesenheitPercentage.percentage')}
          originalValue={values.anwesenheitPercentage.originalPercentage}
          suffix={t.generic.percent}
          decimal
        />
        <ValueInput
          label={t.survey.prebuildType.types.DESKSHARING}
          value={getDesksharingFactor(values.desksharingPercentage.percentage)}
          onChange={setDesksharingQuote}
          decimal
          originalValue={getDesksharingFactor(values.desksharingPercentage.originalPercentage)}
        />
        <ValueInput
          label={t.survey.prebuildType.types.EINZELBUEROS}
          value={values[PrebuildType.EINZELBUEROS].amount}
          onChange={updateValue('EINZELBUEROS.amount')}
          originalValue={values[PrebuildType.EINZELBUEROS].originalAmount}
        />
        <ValueInput
          label={t.survey.analysis.calculation.values.stauflaeche.headline}
          value={values.STAUFLAECHE.practicalQM}
          onChange={updateValue('STAUFLAECHE.practicalQM')}
          originalValue={values.STAUFLAECHE.originalPracticalQM}
          suffix={t.generic.squareMeterSuffix}
          decimal
        />
        <ValueInput
          label={t.survey.analysis.calculation.values.stauflaeche.headlineLaufmeter}
          value={getStaufleacheLaufmeter(values.STAUFLAECHE.practicalQM)}
          originalValue={getStaufleacheLaufmeter(values.STAUFLAECHE.originalPracticalQM)}
          suffix={t.generic.meterSuffix}
          decimal
        />
        <ValueInput
          label={t.survey.analysis.calculation.values.WORKSTATIONS.headline}
          value={values.WORKSTATIONS.amount}
          onChange={updateValue('WORKSTATIONS.amount')}
          originalValue={values.WORKSTATIONS.originalAmount}
        />
        {/* <ValueInput
          label={t.survey.analysis.calculation.values.schreibtische.headline}
          value={values.ergebnisse.schreibtische.practicalAmount}
          suffix={t.generic.amountSuffix}
        /> */}
        <ValueInput
          label={t.survey.analysis.concept.summary.modules.arbeitsplaetzeLabel}
          value={values.ergebnisse.schreibtische.practicalAmount}
          suffix={t.generic.amountSuffix}
        />

        <ValueInput
          label={t.survey.analysis.concept.summary.modules.arbeitsorteLabel}
          value={
            values.ergebnisse.EINZELARBEIT.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR.practicalSpaceAmount +
            values.ergebnisse.MEETING_GROESSE_BESPRECHUNG.PERSON_2.practicalSpaceAmount +
            values.ergebnisse.MEETING_GROESSE_BESPRECHUNG.PERSON_3_4.practicalSpaceAmount +
            values.ergebnisse.MEETING_GROESSE_BESPRECHUNG.PERSON_5_8.practicalSpaceAmount +
            values.ergebnisse.MEETING_GROESSE_BESPRECHUNG.PERSON_9_12.practicalSpaceAmount +
            values.ergebnisse.MEETING_GROESSE_BESPRECHUNG.PERSON_13_16.practicalSpaceAmount +
            values.ergebnisse.MEETING_GROESSE_COWORKING.PERSON_2_4.practicalSpaceAmount +
            values.ergebnisse.MEETING_GROESSE_COWORKING.PERSON_5_8.practicalSpaceAmount +
            values.ergebnisse.MEETING_GROESSE_COWORKING.PERSON_9_12.practicalSpaceAmount +
            values.ergebnisse.MEETING_GROESSE_COWORKING.PERSON_13_16.practicalSpaceAmount +
            values.ergebnisse.HYBRID.XS.practicalSpaceAmount +
            values.ergebnisse.HYBRID.S.practicalSpaceAmount +
            values.ergebnisse.HYBRID.M.practicalSpaceAmount +
            values.ergebnisse.HYBRID.L.practicalSpaceAmount +
            values.ergebnisse.HYBRID.XL.practicalSpaceAmount
          }
          suffix={t.generic.amountSuffix}
        />
        <ValueInput
          label={t.survey.analysis.concept.summary.modules.arbeitsplaetzeSuffix}
          value={sums.arbeitsplaetze.sum}
          suffix={t.generic.amountSuffix}
        />
      </div>
      <AdditionalConfiguration values={values} updateValue={updateValue} />
      <Sonderbedarfe values={values} updateValue={updateValue} pushValue={pushValue} removeValue={removeValue} />
      <OtherInformation survey={conceptDataCluster.teamSurvey} values={values} />
      <SurveyResults values={values} updateValue={updateValue} />
    </div>
  )
}

export default UnitCalculation
